import { motion } from 'framer-motion';
import { useEffect } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import { useSEO, generateServiceSchema } from '../../utils/seo';

export default function ServicesPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  useSEO({
    title: 'Nos Services | Voyage Elite - Transport Privé Premium Genève Lyon Alpes',
    description: 'Services de transport privé premium Voyage Elite : transferts aéroport, stations de ski, événements, excursions. Mercedes Classe E et Van, chauffeur professionnel, disponible 24/7.',
    keywords: 'services transport premium, transfert aéroport Genève, transport ski Alpes, VTC professionnel Lyon, chauffeur privé événements',
    canonical: '/services',
    ogTitle: 'Services Transport Premium - Voyage Elite',
    ogDescription: 'Découvrez nos services de transport privé : transferts aéroport, stations de ski, événements spéciaux. Service 24/7 avec Mercedes premium.',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'WebPage',
          '@id': `${siteUrl}/services#webpage`,
          url: `${siteUrl}/services`,
          name: 'Nos Services de Transport Premium',
          description: 'Services de transport privé premium vers les Alpes et aéroports',
          inLanguage: 'fr-FR',
          isPartOf: {
            '@id': `${siteUrl}/#website`
          }
        },
        {
          '@type': 'ServicePage',
          '@id': `${siteUrl}/services#servicepage`,
          url: `${siteUrl}/services`,
          name: 'Services de Transport Premium Voyage Elite',
          description: 'Transferts aéroport, stations de ski, événements et excursions',
          inLanguage: 'fr-FR'
        },
        generateServiceSchema(),
        {
          '@type': 'ItemList',
          '@id': `${siteUrl}/services#servicelist`,
          name: 'Liste des Services Voyage Elite',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              item: {
                '@type': 'Service',
                name: 'Transferts Aéroport',
                description: 'Service de transfert depuis/vers les aéroports de Genève, Lyon, Chambéry vers les stations de ski alpines',
                provider: {
                  '@type': 'Organization',
                  name: 'Voyage Elite'
                }
              }
            },
            {
              '@type': 'ListItem',
              position: 2,
              item: {
                '@type': 'Service',
                name: 'Transferts Stations de Ski',
                description: 'Transport premium vers toutes les stations de ski des Alpes françaises et suisses',
                provider: {
                  '@type': 'Organization',
                  name: 'Voyage Elite'
                }
              }
            },
            {
              '@type': 'ListItem',
              position: 3,
              item: {
                '@type': 'Service',
                name: 'Transferts Professionnels',
                description: 'Service VTC pour déplacements professionnels, réunions et conférences',
                provider: {
                  '@type': 'Organization',
                  name: 'Voyage Elite'
                }
              }
            },
            {
              '@type': 'ListItem',
              position: 4,
              item: {
                '@type': 'Service',
                name: 'Événements Spéciaux',
                description: 'Transport premium pour mariages, anniversaires et événements privés',
                provider: {
                  '@type': 'Organization',
                  name: 'Voyage Elite'
                }
              }
            },
            {
              '@type': 'ListItem',
              position: 5,
              item: {
                '@type': 'Service',
                name: 'Excursions & Visites',
                description: 'Découverte des Alpes avec chauffeur-guide professionnel',
                provider: {
                  '@type': 'Organization',
                  name: 'Voyage Elite'
                }
              }
            },
            {
              '@type': 'ListItem',
              position: 6,
              item: {
                '@type': 'Service',
                name: 'Mise à Disposition',
                description: 'Chauffeur et véhicule à votre disposition pour la journée ou demi-journée',
                provider: {
                  '@type': 'Organization',
                  name: 'Voyage Elite'
                }
              }
            }
          ]
        }
      ]
    }
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const services = [
    {
      icon: 'ri-plane-line',
      title: 'Transferts Aéroport',
      description: 'Service premium depuis et vers les aéroports de Chambéry, Lyon, Genève et Grenoble. Suivi de vol en temps réel et accueil personnalisé.',
      features: ['Suivi de vol', 'Accueil personnalisé', 'Aide aux bagages', 'WiFi gratuit'],
      color: 'from-alpine-gold to-amber-500'
    },
    {
      icon: 'ri-mountain-line',
      title: 'Transferts Stations de Ski',
      description: 'Transport confortable vers toutes les stations des Alpes : Courchevel, Méribel, Val Thorens, Les Arcs, La Plagne et plus encore.',
      features: ['Porte-skis', 'Véhicules 4x4', 'Chauffeurs expérimentés', 'Routes de montagne'],
      color: 'from-blue-500 to-cyan-500'
    },
    {
      icon: 'ri-building-line',
      title: 'Transferts Professionnels',
      description: 'Service de transport pour vos déplacements d\'affaires, réunions et événements professionnels. Ponctualité et discrétion garanties.',
      features: ['Ponctualité garantie', 'Factures détaillées', 'Compte entreprise', 'Service VIP'],
      color: 'from-slate-600 to-slate-800'
    },
    {
      icon: 'ri-calendar-event-line',
      title: 'Événements Spéciaux',
      description: 'Transport élégant pour mariages, anniversaires, soirées et tous vos événements importants. Service sur-mesure et véhicules de prestige.',
      features: ['Véhicules prestige', 'Décoration possible', 'Service champagne', 'Itinéraire personnalisé'],
      color: 'from-rose-500 to-pink-600'
    },
    {
      icon: 'ri-map-pin-line',
      title: 'Excursions & Visites',
      description: 'Découvrez les Alpes et la région avec nos circuits touristiques personnalisés. Chauffeurs guides connaissant parfaitement la région.',
      features: ['Circuits sur-mesure', 'Guides locaux', 'Arrêts photos', 'Conseils restaurants'],
      color: 'from-green-500 to-emerald-600'
    },
    {
      icon: 'ri-time-line',
      title: 'Mise à Disposition',
      description: 'Chauffeur et véhicule à votre disposition pour la durée de votre choix. Flexibilité totale pour vos déplacements multiples.',
      features: ['Tarif horaire', 'Flexibilité totale', 'Plusieurs arrêts', 'Disponibilité 24/7'],
      color: 'from-purple-500 to-violet-600'
    }
  ];

  const advantages = [
    {
      icon: 'ri-shield-check-line',
      title: 'Sécurité Maximale',
      description: 'Véhicules récents entretenus régulièrement, chauffeurs professionnels formés aux normes de sécurité les plus strictes.'
    },
    {
      icon: 'ri-star-line',
      title: 'Service Premium',
      description: 'Attention aux détails, confort optimal, et service personnalisé pour une expérience de transport d\'exception.'
    },
    {
      icon: 'ri-customer-service-2-line',
      title: 'Disponibilité 24/7',
      description: 'Notre équipe est à votre écoute jour et nuit pour répondre à tous vos besoins de transport, même en urgence.'
    },
    {
      icon: 'ri-price-tag-3-line',
      title: 'Tarifs Transparents',
      description: 'Prix fixes annoncés à l\'avance, sans surprise. Devis gratuit et détaillé pour tous vos trajets.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-night-blue via-slate-800 to-night-blue overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: 'spring' }}
              className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-alpine-gold to-amber-500 rounded-full mb-6 shadow-2xl"
            >
              <i className="ri-service-line text-4xl text-white"></i>
            </motion.div>
            
            <h1 className="font-playfair text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6">
              Nos Services
            </h1>
            <p className="font-inter text-xl md:text-2xl text-white/90 max-w-3xl mx-auto leading-relaxed">
              Des solutions de transport premium adaptées à tous vos besoins dans les Alpes
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 bg-gradient-to-b from-white to-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-playfair text-4xl md:text-5xl font-bold text-night-blue mb-6">
              Nos Services Premium
            </h2>
            <p className="font-inter text-xl text-slate-600 max-w-3xl mx-auto">
              Des solutions de transport adaptées à tous vos besoins
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden"
              >
                <div className={`h-2 bg-gradient-to-r ${service.color}`}></div>
                
                <div className="p-8">
                  <div className={`inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br ${service.color} rounded-xl mb-6 shadow-lg group-hover:scale-110 transition-transform`}>
                    <i className={`${service.icon} text-3xl text-white`}></i>
                  </div>
                  
                  <h3 className="font-playfair text-2xl font-bold text-night-blue mb-4">
                    {service.title}
                  </h3>
                  
                  <p className="font-inter text-slate-600 mb-6 leading-relaxed">
                    {service.description}
                  </p>
                  
                  <ul className="space-y-3">
                    {service.features.map((feature) => (
                      <li key={feature} className="flex items-center gap-3 font-inter text-sm text-slate-700">
                        <i className="ri-checkbox-circle-fill text-alpine-gold text-lg"></i>
                        {feature}
                      </li>
                    ))}
                  </ul>
                  
                  <a
                    href="/reservation"
                    className="mt-6 inline-flex items-center gap-2 text-alpine-gold font-inter font-semibold hover:gap-3 transition-all cursor-pointer"
                  >
                    Réserver ce service
                    <i className="ri-arrow-right-line"></i>
                  </a>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Advantages Section */}
      <section className="py-20 bg-gradient-to-br from-night-blue to-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-playfair text-4xl md:text-5xl font-bold text-white mb-6">
              Pourquoi Choisir Alpine Transfer ?
            </h2>
            <p className="font-inter text-xl text-white/80 max-w-3xl mx-auto">
              L'excellence du service à chaque trajet
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {advantages.map((advantage, index) => (
              <motion.div
                key={advantage.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-alpine-gold to-amber-500 rounded-full mb-6 shadow-xl">
                  <i className={`${advantage.icon} text-4xl text-white`}></i>
                </div>
                
                <h3 className="font-playfair text-2xl font-bold text-white mb-4">
                  {advantage.title}
                </h3>
                
                <p className="font-inter text-white/80 leading-relaxed">
                  {advantage.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-alpine-gold to-amber-500">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            <h2 className="font-playfair text-4xl md:text-5xl font-bold text-night-blue mb-6">
              Prêt à Réserver Votre Transfert ?
            </h2>
            <p className="font-inter text-xl text-night-blue/80 mb-8">
              Réservez en ligne en quelques clics ou contactez-nous pour un devis personnalisé
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/reservation"
                className="px-8 py-4 bg-night-blue text-white rounded-full font-inter font-bold text-lg hover:bg-slate-800 hover:scale-105 transition-all shadow-xl whitespace-nowrap cursor-pointer"
              >
                Réserver maintenant
              </a>
              <a
                href="/contact"
                className="px-8 py-4 bg-white text-night-blue rounded-full font-inter font-bold text-lg hover:bg-slate-50 hover:scale-105 transition-all shadow-xl whitespace-nowrap cursor-pointer"
              >
                Demander un devis
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
